/**
 * @file useGridNav.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-08-13
 * @description Keyboard navigation for a spreadsheet-style grid of inputs.
 *
 *              Nothing like this existed in the app — every other editable table
 *              is click-a-cell-then-tab. People coming off a spreadsheet expect
 *              arrows to move, Enter to go down and Tab to go right, and the
 *              blend calculator and the monthly cost update are both grids they
 *              will live in. Hence a shared hook rather than a one-off.
 *
 *              Usage: give the wrapping element the returned onKeyDown, and
 *              register each input with registerCell(rowKey, colKey).
 *
 *                const grid = useGridNav({ rowKeys, colKeys, onCommit });
 *                <div {...grid.containerProps}>
 *                  <input ref={grid.registerCell(rowKey, colKey)}
 *                         onFocus={() => grid.setActive(rowKey, colKey)} />
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export default function useGridNav({
  rowKeys = [],
  colKeys = [],
  onCommit = null,     // (rowKey, colKey) => void — fired when a cell is left
  onEscape = null,     // (rowKey, colKey) => void — revert this cell
  onLastRowEnter = null, // Enter on the bottom row: usually focus "add material"
  pageSize = 10
} = {}) {
  const cellRefs = useRef(new Map());
  const [active, setActiveState] = useState({ row: null, col: null });
  // Focus is applied in an effect keyed on `active`, but only when we moved the
  // selection ourselves — otherwise every re-render would steal focus back from
  // wherever the user actually clicked.
  const shouldFocus = useRef(false);

  const cellKey = (rowKey, colKey) => `${rowKey} ${colKey}`;

  const registerCell = useCallback((rowKey, colKey) => (el) => {
    const key = cellKey(rowKey, colKey);
    if (el) cellRefs.current.set(key, el);
    else cellRefs.current.delete(key);
  }, []);

  const setActive = useCallback((row, col) => {
    setActiveState({ row, col });
  }, []);

  const moveTo = useCallback((row, col) => {
    if (row === undefined || col === undefined || row === null || col === null) return;
    shouldFocus.current = true;
    setActiveState({ row, col });
  }, []);

  useEffect(() => {
    if (!shouldFocus.current) return;
    shouldFocus.current = false;
    const el = cellRefs.current.get(cellKey(active.row, active.col));
    if (el) {
      el.focus();
      // Select on arrival so typing replaces rather than appends — the
      // spreadsheet behaviour people expect.
      if (typeof el.select === 'function') el.select();
    }
  }, [active]);

  const indexes = useMemo(() => ({
    rowIndex: (r) => rowKeys.indexOf(r),
    colIndex: (c) => colKeys.indexOf(c)
  }), [rowKeys, colKeys]);

  const step = useCallback((dRow, dCol, { wrap = false } = {}) => {
    const r = indexes.rowIndex(active.row);
    const c = indexes.colIndex(active.col);
    if (r < 0 || c < 0) return false;

    let nr = r + dRow;
    let nc = c + dCol;

    if (wrap) {
      if (nc >= colKeys.length) { nc = 0; nr += 1; }
      if (nc < 0) { nc = colKeys.length - 1; nr -= 1; }
    }

    if (nr < 0 || nr >= rowKeys.length) return false;
    if (nc < 0 || nc >= colKeys.length) return false;

    moveTo(rowKeys[nr], colKeys[nc]);
    return true;
  }, [active, colKeys, rowKeys, indexes, moveTo]);

  /**
   * Is the caret at the very start / end of the input's text?
   *
   * Horizontal arrows only leave the cell from its edges. Without this you
   * cannot edit the middle of a number — the single most common complaint about
   * hand-rolled grids, and the reason most of them get abandoned.
   */
  const caretAtStart = (el) =>
    !el || (el.selectionStart === 0 && el.selectionEnd === 0);
  const caretAtEnd = (el) =>
    !el || (el.selectionStart === el.value.length && el.selectionEnd === el.value.length);

  const onKeyDown = useCallback((event) => {
    const { key, shiftKey, ctrlKey, metaKey } = event;
    const el = event.target;
    const isCell = el && el.dataset && el.dataset.gridCell === 'true';
    if (!isCell) return;

    const commit = () => { if (onCommit) onCommit(active.row, active.col); };

    switch (key) {
      case 'ArrowUp':
        event.preventDefault(); commit(); step(-1, 0); break;

      case 'ArrowDown':
        event.preventDefault(); commit(); step(1, 0); break;

      case 'ArrowLeft':
        if (!caretAtStart(el)) return;   // let the caret move inside the value
        event.preventDefault(); commit(); step(0, -1); break;

      case 'ArrowRight':
        if (!caretAtEnd(el)) return;
        event.preventDefault(); commit(); step(0, 1); break;

      case 'Enter': {
        event.preventDefault();
        commit();
        const moved = step(shiftKey ? -1 : 1, 0);
        // Enter on the last row goes on to whatever comes next — for the blend
        // grid that is the "add material" box, so a whole recipe can be typed
        // without touching the mouse.
        if (!moved && !shiftKey && onLastRowEnter) onLastRowEnter();
        break;
      }

      case 'Tab':
        // Without preventDefault the browser walks focus out of the grid
        // entirely and into the toolbar.
        event.preventDefault(); commit(); step(0, shiftKey ? -1 : 1, { wrap: true }); break;

      case 'Home':
        event.preventDefault(); commit();
        if (ctrlKey || metaKey) moveTo(rowKeys[0], colKeys[0]);
        else moveTo(active.row, colKeys[0]);
        break;

      case 'End':
        event.preventDefault(); commit();
        if (ctrlKey || metaKey) moveTo(rowKeys[rowKeys.length - 1], colKeys[colKeys.length - 1]);
        else moveTo(active.row, colKeys[colKeys.length - 1]);
        break;

      case 'PageUp': {
        event.preventDefault(); commit();
        const r = Math.max(0, indexes.rowIndex(active.row) - pageSize);
        moveTo(rowKeys[r], active.col); break;
      }

      case 'PageDown': {
        event.preventDefault(); commit();
        const r = Math.min(rowKeys.length - 1, indexes.rowIndex(active.row) + pageSize);
        moveTo(rowKeys[r], active.col); break;
      }

      case 'Escape':
        if (onEscape) { event.preventDefault(); onEscape(active.row, active.col); }
        break;

      default:
        break;
    }
  }, [active, colKeys, rowKeys, indexes, step, moveTo, onCommit, onEscape, onLastRowEnter, pageSize]);

  const focusCell = useCallback((rowKey, colKey) => moveTo(rowKey, colKey), [moveTo]);

  return {
    active,
    setActive,
    focusCell,
    registerCell,
    onKeyDown,
    // Spread onto the element wrapping the grid. One handler for the whole
    // grid rather than one per input — events bubble from the cells.
    containerProps: { onKeyDown }
  };
}
